// Date Formatter Utility
// Formats dates in user's local timezone

/**
 * Format date to user's local timezone
 * @param {string|Date} date - Date string or Date object
 * @param {string} format - 'full', 'short', 'time', 'relative'
 * @returns {string} Formatted date string
 */
export function formatDate(date, format = 'full') {
  if (!date) return 'N/A';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if valid date
  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }

  switch (format) {
    case 'full':
      // e.g., "Jan 15, 2024 at 2:30 PM"
      return dateObj.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

    case 'short':
      // e.g., "Jan 15, 2024"
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

    case 'time':
      // e.g., "2:30 PM"
      return dateObj.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

    case 'relative':
      // e.g., "2 hours ago", "just now"
      return formatRelativeTime(dateObj);

    default:
      return dateObj.toLocaleString();
  }
}

/**
 * Format date as relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date) {
  if (!date) return 'N/A';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now - dateObj;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;

  // For older dates, show the actual date
  return formatDate(dateObj, 'short');
}

/**
 * Format date for display with timezone info
 */
export function formatDateWithTimezone(date) {
  if (!date) return 'N/A';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const formatted = dateObj.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });

  return formatted;
}

/**
 * Get user's timezone
 */
export function getUserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format ISO date from server to local date
 */
export function parseServerDate(dateString) {
  if (!dateString) return null;

  // Server should return ISO 8601 format (UTC)
  // JavaScript will automatically convert to local timezone
  return new Date(dateString);
}

export default {
  formatDate,
  formatRelativeTime,
  formatDateWithTimezone,
  getUserTimezone,
  parseServerDate
};
